import React, { useState, Dispatch, useCallback, useMemo, useEffect, useRef, JSX } from 'react';
import { ExportModel, GetSalesOrderQueryParams, SalesOrderStatusGroupDto } from 'src/models/salesorder';
import { Products, useGetProductLevel } from 'src/@core/utils/getProductLevel';
import VigoDateRange, { IVigoDateRangeReturn } from 'src/@vigo/vigo-daterange';
import { Box, Button, IconButton, Stack, Typography, useTheme } from '@mui/material';
import { DatabaseExportOutline } from 'mdi-material-ui';
import { useVigoTranslation } from 'src/@core/hooks/useVigoTranslation';
import { calculateRange } from 'src/@core/utils/getDateRange';
import { useRouter } from 'next/router';
import { subscribeToPanelEvent } from 'src/@core/utils/panelEvents';
import parseFilterStringToDateRange from 'src/views/dashboards/helpers/parseFilterStringToDateRange';
import FilterBySelect, { IFilterBySelectReturn } from './components/filterbyselect';
import { useVigoCamelCase } from 'src/@core/hooks/useVigoCamelCase';
import { useGetCombinedSalesOrderConfigDataQuery } from 'src/queries/uiflow/orders';
import { getEtaValues, getRangeUnit, getRoundingValue } from 'src/@core/utils/eta-utils';
import { format } from 'date-fns';
import { RenderProgress } from './components/renderProgress';
import { useAppDispatch, useAppSelector } from 'src/store/baseHooks';
import { useAuth } from 'react-oidc-context';
import { UIComponentProps } from 'src/views/uiflow/util/types';
import { useQueryParams } from 'src/@core/hooks/useQueryParams';
import { useStateWithRef } from 'src/@core/hooks/useStateWithRef';
import { useSettings } from 'src/@core/hooks/useSettings';
import { VigoTooltip } from '../vigo-tooltip';
import { DataGridAction } from '../vigo-datagrid/datagrid-types';
import StatusChip from './components/StatusChip';
import UIFlowListNew from 'src/views/uiflow/util/UIFlowListNew';
import { GridCustomToolbarComponent, NewColumn, RemoteOperation } from '../vigo-datagridNew/datagrid-types';
import { ColumnCellTemplateData } from 'devextreme/ui/data_grid_types';
import CustomStore from 'devextreme/data/custom_store';
import {
  calculatePageNumber,
  combineFilterStrings,
  generateFilterString,
  generateSortString
} from '../vigo-datagridNew/helpers/helpers';
import { useGetUserProfileQuery } from 'src/queries/services/identity';
import PrintLabels from '../vigo-helpers/printLabels';
import { useVigoToast } from 'src/@core/hooks/useVigoToast';
import { setFlowDataIdArray } from 'src/store/slices/uiFlowSlice';
import { postExportRequest } from 'src/services/systemconfig';
import useImportTools from 'src/views/uiflow/import/importTemplate/components/useImportTools';
import { LoadOptions } from 'devextreme/data';
import usePrintManifest from '../vigo-orderlist/helpers/printManifest/usePrintManifest';
import { useNewGridApiRefContext } from 'src/@core/context/newGridRefContext';
import { WarehouseStatusChip } from './components/warehouseStatusChip';
import { AllowedFileTypes } from 'src/views/uiflow/import/importTemplate/components/useImportTools/ImportDropzone';
import { useAccountConfiguration } from 'src/@core/hooks/useAccountConfiguration';
import useImpersonateSessionData from 'src/@core/hooks/useImpersonateSessionData';
import { safeJsonParse } from 'src/@core/utils/safeJsonParse';
import { defaultEtaConfig } from 'src/views/uiflow/generalconfig/GeneralConfigProfile';
import { useQuery } from '@tanstack/react-query';
import { getSalesOrderStatusGroups } from 'src/services/salesorder';

export interface layoutFiles {
  location: string;
  property: string;
}

export interface filter {
  name: string;
  property: string;
  value: number;
  compareType: string;
  defaultGridState?: any;
}

export interface hideColumnByProduct {
  product: Products;
  property: string[];
}

interface FilteredListProps extends UIComponentProps {
  isItemView?: boolean;
  setIsItemView?: Dispatch<boolean>;
  showFilters: boolean;
  showPrint?: boolean;
  parentFilter?: string;
  contained?: boolean;
  controlname: string;
  showExport?: boolean;
  showSearch?: boolean;
  showHistory?: boolean;
  showTracking?: boolean;
  showLayoutReset?: boolean;
  customEdit?: boolean;
  showDateColumnFilter?: boolean;
  hideFooter?: boolean;
  showFilterPanel?: boolean;
  filterOverrideColumn?: string;
  filterOverrideDateRange?: string;
  serverSideExport?: boolean;
  multiSelect?: boolean;
  AdvancedSearch?: (props: any) => JSX.Element | null;
  listFilters?: (props: any) => JSX.Element | null;
  listQueryFn: (params: any) => Promise<any>;
  actions?: DataGridAction[];
  customDefaultGridState?: any;
  customCollectionGridState?: any;
  customDeliveryGridState?: any;
  disableEdit?: boolean;
  disableEffects?: boolean;
  showColumnPicker?: boolean;
  hideHeader?: boolean;
  layoutFiles?: layoutFiles[];
  columnFilters?: filter[];
  hasUATRecords?: boolean;
  hideColumnByProduct?: hideColumnByProduct[];
  customActions?: DataGridAction[];
  ignoreDates?: boolean;
  hideDateRange?: boolean;
  hideActions?: boolean;
  customFilter?: string;
  childList?: boolean;
  exportService?: string;
  exportType?: string;
  importDtoId?: string;
  allowImport?: boolean;
  keyExpr?: string;
  editToolTip?: string;
  height?: string;
  enableStatusGroupsQuery: boolean;
  showPrintManifest?: boolean;
  enableDatelessQueryPaging?: boolean;
  isWarehouse?: boolean;
  excludedSearchProperties?: string[];
  toolbarButtons?: GridCustomToolbarComponent[];
  importAllowedFileTypes?: AllowedFileTypes[];
  showHeaderFilter?: boolean;
  columnCellRenderers?: Record<string, Partial<NewColumn>>;
}

export interface statusProgress {
  value: string;
  colour: string;
  percentage: number;
  icon: string;
  iconcolour: string;
  noofdiscrep: boolean;
  discrepancyColour: string;
  discrepancyIcon: string;
  discrepancyIconColour: string;
}

const PrintButton = () => {
  const theme = useTheme();
  const { t } = useVigoTranslation();
  const { vigoToast } = useVigoToast({});

  const selectedIds = useAppSelector(state => state.attachmentsData.selectedEntityIds);

  const handlePrintLabels = useCallback(() => {
    PrintLabels(selectedIds, vigoToast);
  }, [selectedIds, vigoToast]);

  if (selectedIds.length === 0) return null;

  return (
    <VigoTooltip title={'Print Labels'}>
      <Button variant={theme.palette.mode === 'light' ? 'text' : 'contained'} onClick={handlePrintLabels}>
        {t('Print Labels')}
      </Button>
    </VigoTooltip>
  );
};

const remoteOptions: RemoteOperation = {
  paging: true,
  sorting: true,
  filtering: true
};

const printMultiButton = <PrintButton />;

const emptySearchProperties: string[] = [];
const emptyColumnCellRenderers: Record<string, Partial<NewColumn>> = {};

const VigoFilteredList = ({
  showFilters,
  contained,
  parentFilter,
  controlname,
  showDateColumnFilter,
  showExport,
  showSearch,
  hideFooter,
  showFilterPanel,
  showLayoutReset = true,
  filterOverrideColumn,
  filterOverrideDateRange,
  serverSideExport,
  multiSelect,
  showColumnPicker,
  AdvancedSearch,
  listFilters,
  listQueryFn,
  disableEdit,
  disableEffects = false,
  hideHeader = false,
  columnFilters,
  hasUATRecords = false,
  hideColumnByProduct,
  customActions,
  importDtoId,
  allowImport,
  ignoreDates = false,
  customDefaultGridState,
  hideActions = false,
  customFilter = '',
  childList = false,
  exportType = '',
  exportService = '',
  keyExpr,
  editToolTip = '',
  height,
  enableStatusGroupsQuery = false,
  showPrintManifest = false,
  enableDatelessQueryPaging = false,
  isWarehouse,
  excludedSearchProperties = emptySearchProperties,
  toolbarButtons,
  importAllowedFileTypes = [],
  showHeaderFilter = true,
  columnCellRenderers = emptyColumnCellRenderers
}: FilteredListProps) => {
  // State
  const [overrideFilter, setOverrideFilter] = useState<boolean>(false);

  // Refs
  const userEmailRef = useRef<string>('');
  const isDeletedFilterRef = useRef<boolean>(false);
  const totalCountRef = useRef<number>(0);
  const pageNumberRef = useRef<number>(0);
  const exportFilterRef = useRef<string>('');

  // Hooks
  const { t } = useVigoTranslation();
  const router = useRouter();
  const uiFlowDispatch = useAppDispatch();
  const { toCamelCase } = useVigoCamelCase();
  const { removeQueryParam } = useQueryParams();
  const prod = useGetProductLevel();
  const { vigoToast } = useVigoToast({});
  const theme = useTheme();
  const { ManifestRenderContainer, manifestStraightToPrint, containerRef } = usePrintManifest();
  const { state } = useNewGridApiRefContext();
  const { ImportButton, ImportDropzone } = useImportTools({
    dtoId: importDtoId,
    dropZoneDisabled: !allowImport,
    allowedExtensions: importAllowedFileTypes
  });
  const { settings } = useSettings();
  const { getAccountConfigFromSession } = useAccountConfiguration();
  const { isImpersonating, impersonateData } = useImpersonateSessionData();

  const { filter: filterParam = undefined, extrafilter: extraFilterParam = undefined } = router.query;

  const auth = useAuth();
  const user = auth.user;
  const loggedInUserId = user?.profile.tenantUserId as string;
  let partnerId = '';
  // check if we are already impersonating a tenant
  if (isImpersonating) {
    partnerId = impersonateData.partnerId ?? '';
  } else {
    partnerId = (user?.profile?.partnerId as string) ?? '';
  }

  const PAGE_SIZE = 100;

  const { data: userData } = useGetUserProfileQuery(loggedInUserId);

  const { data: customerConfigData } = useGetCombinedSalesOrderConfigDataQuery((partnerId as string) ?? '', {
    enabled: prod === Products.MyPortal && Boolean(partnerId)
  });

  const { data: statusGroups = [] } = useQuery({
    queryKey: ['salesOrderStatusGroups'],
    queryFn: async (): Promise<SalesOrderStatusGroupDto[]> => (await getSalesOrderStatusGroups()).data,
    enabled: enableStatusGroupsQuery,
    refetchOnWindowFocus: false
  });

  if (hasUATRecords) {
    if (settings.showUATRecords === undefined) {
      settings.showUATRecords = false;
    }

    if (settings.showUATRecords) {
      showFilters = false;
    }
  }

  const getCompareType = useCallback(
    (column: string): string => {
      let returnVal = ' datetime';
      columnFilters?.forEach((filter: filter) => {
        if (filter.property.toLocaleLowerCase() === column.toLocaleLowerCase()) {
          returnVal = ' ' + filter.compareType;
        }
      });

      return returnVal;
    },
    [columnFilters]
  );

  function getColumn(columnNo: string): string {
    if (columnNo.length > 1) {
      return columnNo;
    }

    const filter = columnFilters?.find((filter: filter) => filter.value.toString() === columnNo);

    if (!filter) {
      return 'unknown';
    }

    return filter.property;
  }

  const productHiddenColumns = useMemo(() => {
    const hiddenColumnsForMyPortal =
      hideColumnByProduct?.find(item => item.product === Products.MyPortal)?.property ?? [];
    const hiddenColumnsForFlow = hideColumnByProduct?.find(item => item.product === Products.Flow)?.property ?? [];

    return prod === Products.Flow ? hiddenColumnsForFlow : hiddenColumnsForMyPortal;
  }, [hideColumnByProduct, prod]);

  /**
   * Initializes the local storage with default grid states if they don't exist.
   * Grid states for order list, collection date and delivery date are stored separately.
   * When impersonating, this function uses separate localStorage keys with an 'impersonating-' prefix to maintain grid layouts between Flow and myPortal.
   * 'RegularState' corresponds to the gridState when not impersonating.
   *
   * @return {void} This function does not return anything.
   */
  const initStorageV2 = useCallback(() => {
    const gridStateName = 'gridState-';
    let controlName = controlname;
    if (isImpersonating) controlName = `impersonating-${controlname}`;

    const safe = (s?: string) => (s ?? '').toLowerCase().replace(/\s+/g, '');
    const buildKey = (cn: string, prop: string) => `${gridStateName}${cn}-${safe(prop)}-${loggedInUserId}`;

    const parseJson = (val: string | null) => {
      if (!val) return null;
      try {
        return JSON.parse(val);
      } catch {
        return null;
      }
    };

    try {
      (columnFilters ?? []).forEach(filter => {
        const storageKey = buildKey(controlName, filter.property);
        const savedGridState = parseJson(localStorage.getItem(storageKey));

        // If saved state exists, we may need to merge in hidden/impersonation columns later
        if (savedGridState) {
          // If NOT impersonating but an impersonation state exists, ensure hidden columns from impersonation are restored
          if (!isImpersonating) {
            const impKey = buildKey(`impersonating-${controlname}`, filter.property);
            const impState = parseJson(localStorage.getItem(impKey));
            if (impState?.columns && Array.isArray(impState.columns) && productHiddenColumns.length) {
              const currentColumns = Array.isArray(savedGridState.columns) ? [...savedGridState.columns] : [];
              const defaultColumns = (filter.defaultGridState || customDefaultGridState)?.columns || [];
              let changed = false;

              productHiddenColumns.forEach(hiddenCol => {
                const existsInCurrent = currentColumns.find((c: any) => c.dataField === hiddenCol);
                if (!existsInCurrent) {
                  const defaultColumn = defaultColumns.find((c: any) => c.dataField === hiddenCol);
                  if (defaultColumn) {
                    currentColumns.push({ ...defaultColumn, visible: true });
                    changed = true;
                  }
                }
              });

              if (changed) {
                currentColumns.sort((a: any, b: any) => (a.visibleIndex || 0) - (b.visibleIndex || 0));
                localStorage.setItem(storageKey, JSON.stringify({ ...savedGridState, columns: currentColumns }));
              }
            }
          }

          return; // saved state exists -> continue to next filter
        }

        // No saved state -> build default
        let defaultState = filter.defaultGridState ?? customDefaultGridState ?? {};

        // If impersonating and there is a regular state, base impersonation defaults on it (but remove hidden columns)
        if (isImpersonating) {
          const regularKey = buildKey(controlname, filter.property);
          const regularState = parseJson(localStorage.getItem(regularKey));
          if (regularState) {
            // clone and filter out hidden columns for impersonation view
            if (Array.isArray(regularState.columns) && productHiddenColumns.length) {
              const filteredCols = regularState.columns.filter(
                (col: any) => !productHiddenColumns.includes(col.dataField)
              );
              defaultState = { ...regularState, columns: filteredCols };
            } else {
              defaultState = regularState;
            }
          }
        }

        localStorage.setItem(storageKey, JSON.stringify(defaultState));
      });

      // Handle the "no columnFilters" fallback key once
      if (!columnFilters || (columnFilters.length < 1 && customDefaultGridState)) {
        const storageKey = `${gridStateName}${controlName}-${loggedInUserId}`;
        if (!localStorage.getItem(storageKey)) {
          let defaultState = customDefaultGridState ?? {};
          if (isImpersonating) {
            const regularKey = `${gridStateName}${controlname}-${loggedInUserId}`;
            const regularState = parseJson(localStorage.getItem(regularKey));
            if (regularState) {
              if (Array.isArray(regularState.columns) && productHiddenColumns.length) {
                const filteredCols = regularState.columns.filter(
                  (col: any) => !productHiddenColumns.includes(col.dataField)
                );
                defaultState = { ...regularState, columns: filteredCols };
              } else {
                defaultState = regularState;
              }
            }
          } else {
            const impKey = `${gridStateName}impersonating-${controlname}-${loggedInUserId}`;
            const impState = parseJson(localStorage.getItem(impKey));
            if (impState) {
              defaultState = impState;
            }
          }
          localStorage.setItem(storageKey, JSON.stringify(defaultState));
        }
      }
    } catch (e) {
      // localStorage may be unavailable — fail silently but avoid throwing during render
      // optional: telemetry here
    }
  }, [columnFilters, controlname, customDefaultGridState, productHiddenColumns, isImpersonating, loggedInUserId]);

  const defaultDateRange = calculateRange(
    filterOverrideDateRange
      ? filterOverrideDateRange
      : localStorage.getItem('daterange-' + controlname + '-' + loggedInUserId)?.replace(/["]/g, '') ?? 'Today',
    ''
  );

  // State
  const [filterColumn, setFilterColumn] = useState<string>(
    getColumn(
      filterOverrideColumn
        ? filterOverrideColumn
        : localStorage.getItem('ordercolumn-' + controlname + '-' + loggedInUserId)?.replace(/["]/g, '') ?? '0'
    )
  );
  const [orderByIndex, setOrderByIndex] = useState<string>(
    localStorage.getItem('ordercolumn-' + controlname + '-' + loggedInUserId)?.replace(/["]/g, '') ?? '0'
  );
  const [additionalFilter, setAdditionalFilter] = useState<string>('');
  const [, setFilter, filterRef] = useStateWithRef<string>(
    !ignoreDates
      ? filterColumn +
          ' ge ' +
          getCompareType(filterColumn) +
          "'" +
          format(defaultDateRange.dateFrom!, 'yyyy-MM-dd') +
          ' 00:00:00' +
          "' and " +
          filterColumn +
          ' le ' +
          getCompareType(filterColumn) +
          "'" +
          format(defaultDateRange.dateTo!, 'yyyy-MM-dd') +
          ' 23:59:59' +
          "'"
      : 'unknown' + (parentFilter != '' ? (!ignoreDates ? ' and ' : '' + parentFilter) : '')
  );
  const [filterDates, setFilterDates] = useState<IVigoDateRangeReturn>({
    dateFrom: defaultDateRange.dateFrom,
    dateTo: defaultDateRange.dateTo,
    selectedRange: localStorage.getItem('daterange-' + controlname + '-' + loggedInUserId) ?? 'Today'
  });

  const buildLocalFilter = useCallback(() => {
    let localFilter = `${filterRef.current} ${extraFilterParam ? `and ${extraFilterParam}` : ''}`;

    //if we are passing a filtercolumn and range from an external source this overrides internals
    if (filterOverrideColumn && filterOverrideDateRange) {
      localFilter = `${filterOverrideColumn} ge datetime'${format(
        new Date(filterOverrideDateRange.split('-')[0]),
        'yyyy-MM-dd'
      )} 00:00:00' and ${filterOverrideColumn} le datetime'${format(
        new Date(filterOverrideDateRange.split('-')[1]),
        'yyyy-MM-dd'
      )} 23:59:59'`;
    }

    if (!localFilter.includes('isUATConsignment')) {
      if (localFilter != '') {
        localFilter = localFilter + ' and ' + 'isUATConsignment eq ' + settings.showUATRecords;
      } else {
        localFilter = 'isUATConsignment eq ' + settings.showUATRecords;
      }
    }

    return localFilter;
  }, [extraFilterParam, filterOverrideColumn, filterOverrideDateRange, filterRef, settings.showUATRecords]);

  /**
   * Returns an estimated time of arrival string based on the input ETA string.
   * For Flow, as a customer configuration is not available, default values are used to get a basic formatted ETA.
   *
   * @param {string} eta - the estimated time of arrival string in ISO 8601 format
   * @return {string} the estimated time of arrival in the format specified by the customer configuration
   */
  const getETA = useCallback(
    (eta: string): string => {
      if (!eta) {
        return '';
      }

      const m = new Date(eta);
      const momentTime = format(m, 'HH:mm');

      if (prod === Products.Flow) {
        // Are we an AALCO depot and is the aalcoEtaWindow toggle enabled in settings?
        if (getAccountConfigFromSession().hasConsolidatedNotifications && settings.aalcoEtaWindow) {
          // Show a 1 hour window for AALCO depots
          const startTime = format(new Date(m.getTime() - 60 * 60 * 1000), 'HH:mm');
          const endTime = format(new Date(m.getTime() + 60 * 60 * 1000), 'HH:mm');

          // Return the formatted ETA as a range
          return `${startTime} - ${endTime}`;
        } else {
          // just return the momentTime
          return momentTime;
        }
      }

      // if isMyPortal is true and customerConfigData is not available, return the momentTime
      if (prod === Products.MyPortal && (!customerConfigData || !customerConfigData.generalConfiguration?.profile)) {
        return momentTime;
      }

      const etaString = getEtaValues(
        {
          show:
            customerConfigData?.generalConfiguration?.profile?.etaDisplayType === 1 ||
            customerConfigData?.generalConfiguration?.profile?.etaDisplayType === 2,
          use: true,
          rounding: getRoundingValue(
            customerConfigData?.generalConfiguration?.profile?.etaRangeRoundingType?.toString() ??
              defaultEtaConfig.etaRangeRoundingType
          ),
          range: customerConfigData?.generalConfiguration?.profile?.etaDisplayType === 2,
          rangeValue:
            customerConfigData?.generalConfiguration?.profile?.etaDisplayRangeValue?.toString() ??
            defaultEtaConfig.etaDisplayRangeValue,
          rangeUnit: getRangeUnit(
            customerConfigData?.generalConfiguration?.profile?.etaDisplayRangeUnit?.toString() ??
              defaultEtaConfig.etaDisplayRangeUnit,
            t
          ),
          threshold:
            customerConfigData?.generalConfiguration?.profile?.etaDisplaySetThreshold ??
            defaultEtaConfig.etaDisplaySetThreshold,
          lowThreshold:
            customerConfigData?.generalConfiguration?.profile?.etaDisplayLowThreshold ??
            defaultEtaConfig.etaDisplayLowThreshold,
          highThreshold:
            customerConfigData?.generalConfiguration?.profile?.etaDisplayHighThreshold ??
            defaultEtaConfig.etaDisplayHighThreshold
        },
        {
          eta: momentTime
        }
      );

      if (etaString.low && etaString.high) {
        return etaString.low + ' - ' + etaString.high;
      }

      if (etaString.value) {
        return etaString.value;
      }

      return eta;
    },
    [customerConfigData, getAccountConfigFromSession, prod, settings.aalcoEtaWindow, t]
  );

  // make sure column ins not hidden
  const Hidden = useCallback(
    (dataField: string): boolean => {
      let returnValue = false;

      if (hideColumnByProduct && hideColumnByProduct?.length > 0) {
        hideColumnByProduct?.forEach((hide: hideColumnByProduct) => {
          if (hide.product === prod) {
            if (hide.property.includes(dataField)) {
              returnValue = true;

              return;
            }
          }
        });
      }

      return returnValue;
    },
    [hideColumnByProduct, prod]
  );

  /**
   * Checks if the given value is a valid date.
   * @param value - The value to check.
   * @returns {boolean} - Returns true if the value is a valid date, false otherwise.
   */
  const isValidDate = (value: any): boolean => {
    const date = new Date(value);

    return !isNaN(date.getTime());
  };

  const determineDefaultGridState = useMemo(() => {
    const gridStateName = 'gridState-';
    let controlName = controlname;

    if (isImpersonating) {
      controlName = `impersonating-${controlname}`;
    }

    const state = localStorage.getItem(
      gridStateName +
        controlName.toLocaleLowerCase().replace(' ', '') +
        '-' +
        filterColumn.toLocaleLowerCase().replace(' ', '') +
        '-' +
        loggedInUserId
    );
    if (state) {
      return state;
    } else {
      // Do we have defaultGridState?
      if (customDefaultGridState) {
        return JSON.stringify(customDefaultGridState);
      }

      // Do we have columnFilters to check against?
      if (columnFilters && columnFilters.length > 0) {
        const filter = columnFilters.find((flt: filter) => flt.property.toLowerCase() === filterColumn.toLowerCase());
        if (filter && filter.defaultGridState) {
          return JSON.stringify(filter.defaultGridState);
        }
      }

      // Return default object
      return JSON.stringify({});
    }
  }, [columnFilters, controlname, customDefaultGridState, filterColumn, isImpersonating, loggedInUserId]);

  // create array of columns we want in grid
  const columns = useMemo(() => {
    // reuse determineDefaultGridState (already computed) and fall back to deliverydatetime key
    const gridStateName = 'gridState-';
    const controlName = isImpersonating ? `impersonating-${controlname}` : controlname;
    const stateJson =
      determineDefaultGridState ??
      localStorage.getItem(gridStateName + controlName + '-deliverydatetime-' + loggedInUserId) ??
      '{}';

    const safeParse = (s: string) => {
      try {
        return JSON.parse(s);
      } catch {
        return {};
      }
    };

    const gridState = safeParse(stateJson);
    const columnsToDisplay: any[] = Array.isArray(gridState?.columns) ? gridState.columns : [];
    if (columnsToDisplay.length === 0) return [];

    // Get default columns to check for missing columns that should be visible
    const defaultState =
      customDefaultGridState ??
      (columnFilters && columnFilters.length > 0
        ? columnFilters.find((flt: filter) => flt.property.toLowerCase() === filterColumn.toLowerCase())
            ?.defaultGridState
        : null);
    const defaultColumns: any[] = Array.isArray(defaultState?.columns) ? defaultState.columns : [];

    // Add missing columns that should not be hidden
    const mergedColumns = [...columnsToDisplay];
    defaultColumns.forEach((defCol: any) => {
      const existsInCurrent = mergedColumns.find((c: any) => c.dataField === defCol.dataField);
      const isHidden = productHiddenColumns.includes(
        toCamelCase(
          String(defCol.dataField ?? '')
            .split(' ')
            .join('')
        )
      );

      // If column should not be hidden but is missing, add it back
      if (!isHidden && !existsInCurrent) {
        mergedColumns.push({ ...defCol, visible: false });
      }
    });

    const cols: NewColumn[] = mergedColumns
      .filter((col: any) => {
        if (!col || !col.dataField) return false;
        if (isImpersonating) {
          return !Hidden(col.dataField);
        }

        // Filter out columns that should be hidden for this product
        const dataFieldCamel = toCamelCase(
          String(col.dataField ?? '')
            .split(' ')
            .join('')
        );
        if (productHiddenColumns.includes(dataFieldCamel)) {
          return false;
        }

        return true;
      })
      .map((col: any): NewColumn => {
        const dataField = String(col.dataField ?? '');
        const dataFieldLower = dataField.toLowerCase();
        const dataFieldCamel = toCamelCase(dataField.split(' ').join(''));

        const base: Partial<NewColumn> = {
          width: col.width || 'auto',
          headerAlign: 'left',
          visible: dataField === 'Consignment Number' ? false : Boolean(col.visible),
          allowHeaderFiltering: col?.dataType === 'string',
          allowFiltering: col?.dataType === 'string',
          dataType: col.dataType,
          name: col.name,
          caption: col.caption
            ? t(col.caption)
            : col.name && col.name === col.dataField
            ? t(
                dataField[0].toUpperCase() +
                  dataField
                    .substring(1)
                    .replace(/([A-Z])/g, ' $1')
                    .trim()
                    .replace('E T A', 'ETA')
                    .replace('Eta', 'ETA')
              )
            : t(col.name),
          dataField: dataFieldCamel,
          customType:
            col.dataType === 'boolean'
              ? 'boolean'
              : dataField.includes('Date')
              ? 'date'
              : dataField.includes('ETA')
              ? 'ETA'
              : dataField.includes('Time')
              ? 'time'
              : dataField.includes('Spaces')
              ? 'number'
              : dataField.includes('Items')
              ? 'number'
              : dataField.includes('Discrepancies')
              ? 'number'
              : dataField.includes('Weight')
              ? 'number'
              : col.dataType
        };

        // specialized renders (compute once and attach)
        if (dataFieldLower === 'lateststatus') {
          return {
            ...base,
            cellRender({ rowType, value, data }: ColumnCellTemplateData) {
              if (rowType === 'group') return value;

              return (
                <RenderProgress
                  value={value}
                  numOfDiscrep={data['hasDiscrepancies'] ?? false}
                  statusGroups={statusGroups}
                />
              );
            }
          } as NewColumn;
        }

        if (dataFieldLower === 'status' && isWarehouse) {
          return {
            ...base,
            caption: 'Status',
            cellRender({ value }: ColumnCellTemplateData) {
              if (!value) return '';

              return <WarehouseStatusChip value={value} />;
            }
          } as NewColumn;
        }

        if (dataFieldLower === 'customerscanningstatusdescription') {
          return {
            ...base,
            caption: 'Status',
            cellrender({ value }: ColumnCellTemplateData) {
              if (!value) return '';

              return <StatusChip status={value} />;
            }
          } as NewColumn;
        }

        if (dataFieldLower.includes('date')) {
          return {
            ...base,
            cellRender({ value }: ColumnCellTemplateData) {
              if (!value || !isValidDate(value)) return '';

              return format(new Date(value), 'dd/MM/yy');
            }
          } as NewColumn;
        }

        if (dataFieldLower.includes('time')) {
          return {
            ...base,
            cellRender({ data }: ColumnCellTemplateData) {
              const val = data?.[dataFieldCamel];

              if (!val) return '';

              const time = String(val).split(':');

              return `${time[0] || ''}:${time[1] || ''}`;
            }
          } as NewColumn;
        }

        if (dataFieldLower === 'latesteta') {
          return {
            ...base,
            cellRender({ data }: ColumnCellTemplateData) {
              const val = data?.[dataFieldCamel];
              if (!val) return '';

              return (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', flexDirection: 'column' }}>
                      <Typography noWrap component="a" variant="body2" sx={{ textDecoration: 'none' }}>
                        {getETA(val)}
                      </Typography>
                      <Typography noWrap component="a" variant="caption">
                        {isValidDate(val) ? new Date(val).toLocaleDateString() : 'Invalid date'}
                      </Typography>
                    </Box>
                  </Box>
                </>
              );
            }
          } as NewColumn;
        }

        if (dataFieldLower === 'additionalreferences') {
          return {
            ...base,
            cellRender({ data }: ColumnCellTemplateData) {
              if (!data.additionalReferences) {
                return '';
              }

              const formattedReferences = data.additionalReferences
                .split(',')
                .map((ref: string) => ref.trim())
                .filter((ref: string) => ref !== '')
                .join(', ');

              return formattedReferences;
            }
          } as NewColumn;
        }

        if (dataFieldLower in columnCellRenderers) {
          return {
            ...base,
            ...columnCellRenderers[dataFieldLower]
          } as NewColumn;
        }

        return base as NewColumn;
      });

    if (cols.length > 0) cols[cols.length - 1].width = '100%';

    return cols;
  }, [
    Hidden,
    columnCellRenderers,
    columnFilters,
    controlname,
    customDefaultGridState,
    determineDefaultGridState,
    filterColumn,
    getETA,
    isImpersonating,
    isWarehouse,
    loggedInUserId,
    productHiddenColumns,
    statusGroups,
    t,
    toCamelCase
  ]);

  const buildFilterString = useCallback(
    (dateRange: IVigoDateRangeReturn, column: string, additionalFilter: string, useAsFilter?: boolean) => {
      let filterString = '';

      if (useAsFilter) {
        setOverrideFilter(true);

        return additionalFilter;
      }

      const storedDateRange = calculateRange(
        localStorage.getItem('daterange-' + controlname + '-' + loggedInUserId) ?? 'Today',
        ''
      );

      const range = dateRange ? dateRange : storedDateRange;

      if (range.dateFrom != undefined && column != undefined && !ignoreDates) {
        filterString =
          filterString +
          column +
          ' ge' +
          getCompareType(filterColumn) +
          "'" +
          format(range.dateFrom!, 'yyyy-MM-dd') +
          ' 00:00:00' +
          "' and " +
          column +
          ' le' +
          getCompareType(filterColumn) +
          "'" +
          format(range.dateTo!, 'yyyy-MM-dd') +
          ' 23:59:59' +
          "'";
      }

      if (filterString != '' && additionalFilter != '') {
        filterString = filterString + ' and ';
      }
      if (additionalFilter != '') {
        filterString = filterString + additionalFilter;
      }

      if (parentFilter != '') {
        if (filterString != '') {
          filterString = filterString + ' and ';
        }
        filterString = filterString + parentFilter;
      }

      return filterString;
    },
    [controlname, filterColumn, getCompareType, ignoreDates, loggedInUserId, parentFilter]
  );

  const handleDateChange = useCallback(
    (dateRange: IVigoDateRangeReturn) => {
      const datesObject = {
        dateFrom: dateRange.dateFrom,
        dateTo: dateRange.dateTo,
        selectedRange: dateRange.selectedRange
      };

      if (!isValidDate(datesObject.dateFrom) || !isValidDate(datesObject.dateTo)) {
        return;
      }

      setFilterDates(datesObject);

      localStorage.setItem('daterange-' + controlname + '-' + loggedInUserId, JSON.stringify(dateRange.selectedRange));

      setFilter(buildFilterString(datesObject, filterColumn, additionalFilter));
      removeQueryParam('filter');
    },
    [
      additionalFilter,
      buildFilterString,
      controlname,
      filterColumn,
      loggedInUserId,
      removeQueryParam,
      setFilter,
      setFilterDates
    ]
  );

  const orderByChange = useCallback(
    (column: IFilterBySelectReturn) => {
      if (column.orderByColumn === 'deletedOn') {
        isDeletedFilterRef.current = true;
      } else {
        isDeletedFilterRef.current = false;
      }
      setOrderByIndex(`${column.orderByIndex}`);
      setFilterColumn(column.orderByColumn);
      localStorage.setItem('ordercolumn-' + controlname + '-' + loggedInUserId, JSON.stringify(column.orderByIndex));
      setFilter(buildFilterString(filterDates, column.orderByColumn, additionalFilter));
      removeQueryParam('filter');
    },
    [additionalFilter, buildFilterString, controlname, filterDates, loggedInUserId, removeQueryParam, setFilter]
  );

  const handleExport = useCallback((): void => {
    const exportColumns = new Map<string, string>();
    const gridStateName = 'gridState-';
    let controlName = controlname;

    if (isImpersonating) {
      controlName = `impersonating-${controlname}`;
    }

    const gridState = safeJsonParse(
      localStorage.getItem(gridStateName + controlName + '-' + filterColumn.toLowerCase() + '-' + loggedInUserId)
    );

    // get columns
    const columns = gridState?.columns ?? [];

    // order by visibleIndex
    columns.sort((a: any, b: any) => {
      return a.visibleIndex - b.visibleIndex;
    });

    if (columns) {
      columns.forEach((column: any) => {
        if (column.visible === true && column.dataField !== '') {
          exportColumns.set(column.dataField, column.dataField);
        }
      });

      const exportModel: ExportModel = {
        filter: exportFilterRef?.current == '' ? filterRef.current : exportFilterRef.current,
        showDeleted: false,
        ignoreOwner: false,
        notifyOnCompletion: userEmailRef.current ?? '',
        columns: Object.fromEntries(exportColumns)
      };

      // call service
      postExportRequest(exportModel, exportService, exportType)
        .then(() => {
          vigoToast.success('Export link will be sent to your email shortly.');
        })
        .catch(() => {
          vigoToast.error('There was an error exporting the data.');
        });
    }
  }, [controlname, exportService, exportType, filterColumn, filterRef, loggedInUserId, vigoToast, isImpersonating]);

  const exportButton = useMemo(
    () => (
      <VigoTooltip title={'Export to CSV'}>
        <IconButton
          sx={{ cursor: 'pointer', paddingTop: 0, paddingBottom: 0, paddingLeft: 2, paddingRight: 2 }}
          component="label"
          onClick={handleExport}>
          <DatabaseExportOutline />
        </IconButton>
      </VigoTooltip>
    ),
    [handleExport]
  );

  const orderByButton = useMemo(
    () => (
      <Stack direction="column" justifyContent="center" height="100%">
        <FilterBySelect
          selectedValue={orderByIndex}
          key={1}
          onChange={orderByChange}
          passedInOptions={columnFilters}></FilterBySelect>
      </Stack>
    ),
    [columnFilters, orderByChange, orderByIndex]
  );

  const manifestButton = useMemo(() => {
    const handleManifestClick = async () => {
      const selectedRows = state.gridApiRef?.current?.instance()?.getSelectedRowsData() ?? [];
      const selectedRowIds = [...selectedRows.values()].map((row: any) => row.id);
      const columns = state.gridApiRef?.current?.instance()?.getVisibleColumns() ?? [];

      if (selectedRowIds.length > 0 && selectedRowIds.length > 500) {
        vigoToast.error('Please select fewer consignments (Limit is 500)');

        return;
      }

      const filter =
        selectedRowIds.length > 0 ? selectedRowIds.map(id => `id eq guid'${id}'`).join(' or ') : buildLocalFilter();

      const res = await listQueryFn({
        filter: filter,
        pageNumber: 0,
        pageSize: 500
      });

      const rows = res?.data ?? [];
      manifestStraightToPrint(rows, columns, true);
    };

    return (
      <VigoTooltip title={'Print Manifest'}>
        <Button
          variant={theme.palette.mode === 'light' ? 'text' : 'outlined'}
          sx={{
            color: theme.palette.mode === 'dark' ? 'white' : 'initial',
            '&:hover': {
              backgroundColor: theme.palette.mode === 'dark' ? theme.palette.customColors.primaryGradient : ''
            }
          }}
          onClick={() => {
            handleManifestClick();
          }}>
          Print Manifest
        </Button>
      </VigoTooltip>
    );
  }, [
    buildLocalFilter,
    listQueryFn,
    manifestStraightToPrint,
    state.gridApiRef,
    theme.palette.customColors.primaryGradient,
    theme.palette.mode,
    vigoToast
  ]);

  const periodButton = useMemo(
    () => (
      <VigoDateRange
        name={controlname + 'listrange'}
        selectedRange={filterDates.selectedRange}
        showPast={true}
        showFuture={true}
        onChange={handleDateChange}
      />
    ),
    [controlname, filterDates.selectedRange, handleDateChange]
  );

  const headerButtonGroup = useMemo((): GridCustomToolbarComponent[] => {
    const buttons: GridCustomToolbarComponent[] = [];

    if (toolbarButtons) {
      toolbarButtons.forEach((button: GridCustomToolbarComponent) => {
        buttons.push(button);
      });
    }

    if (showDateColumnFilter && !ignoreDates) {
      buttons.push({
        id: 'print',
        name: 'print',
        location: 'before',
        toolbar: 'top',
        visible: !isDeletedFilterRef.current,
        render: () => printMultiButton
      });
      buttons.push({
        id: 'manifest',
        name: 'manifest',
        location: 'before',
        toolbar: 'top',
        visible: showPrintManifest,
        render: () => manifestButton
      });
      buttons.push({
        id: 'orderBy',
        name: 'orderBy',
        location: 'after',
        toolbar: 'top',
        visible: true,
        render: () => orderByButton
      });
      buttons.push({
        id: 'period',
        name: 'period',
        location: 'after',
        toolbar: 'top',
        visible: true,
        render: () => periodButton
      });
      buttons.push({
        id: 'serverexport',
        name: 'Export',
        location: 'after',
        toolbar: 'top',
        visible: true,
        render: () => exportButton
      });
      buttons.push({
        id: 'import',
        name: 'Import',
        location: 'after',
        toolbar: 'top',
        visible: Boolean(allowImport),
        render: () => <ImportButton />
      });
    } else if (ignoreDates) {
      buttons.push({
        id: 'print',
        name: 'print',
        location: 'before',
        toolbar: 'top',
        visible: !isDeletedFilterRef.current,
        render: () => printMultiButton
      });
      buttons.push({
        id: 'serverexport',
        name: 'Export',
        location: 'after',
        toolbar: 'top',
        visible: true,
        render: () => exportButton
      });
    }

    return buttons;
  }, [
    ImportButton,
    allowImport,
    exportButton,
    ignoreDates,
    manifestButton,
    orderByButton,
    periodButton,
    showDateColumnFilter,
    showPrintManifest,
    toolbarButtons
  ]);

  const FilterButtonSelected = useCallback(
    (refilter: string, useAsFilter: boolean): void => {
      setAdditionalFilter(refilter);
      setFilter(buildFilterString(filterDates, filterColumn, refilter, useAsFilter));
    },
    [buildFilterString, filterColumn, filterDates, setFilter]
  );

  const customStore = useMemo(
    () =>
      new CustomStore({
        key: keyExpr ?? 'id',
        load: async (loadOptions: LoadOptions<any>) => {
          // If loadOptions does not have skip and take, then use pageNumberRef
          let pageNumber = 0;
          if (!loadOptions.skip && !loadOptions.take) {
            pageNumber = pageNumberRef.current;
          } else {
            pageNumber = calculatePageNumber(loadOptions, totalCountRef.current, PAGE_SIZE);

            // set page number ref
            pageNumberRef.current = pageNumber;
          }

          // Use the grid's live visible columns for filtering
          const liveVisibleColumns = state.gridApiRef?.current?.instance()?.getVisibleColumns();
          const searchableColumns =
            liveVisibleColumns && liveVisibleColumns.length > 0
              ? liveVisibleColumns
              : columns.filter(col => col?.visible !== false);

          const additionalFilters = generateFilterString(
            loadOptions,
            searchableColumns as any,
            ignoreDates,
            excludedSearchProperties
          );
          const sortString = generateSortString(loadOptions);

          let baseFilter = overrideFilter ? additionalFilter : filterRef.current;

          if (!overrideFilter) {

            if (filterOverrideColumn && filterOverrideDateRange) {
              baseFilter = `${filterOverrideColumn} ge ${getCompareType(filterOverrideColumn)}'${format(
                new Date(filterOverrideDateRange.split('-')[0]),
                'yyyy-MM-dd'
              )} 00:00:00' and ${filterOverrideColumn} le ${getCompareType(filterOverrideColumn)}'${format(
                new Date(filterOverrideDateRange.split('-')[1]),
                'yyyy-MM-dd'
              )} 23:59:59'`;
            }

          }

          let filter = combineFilterStrings(baseFilter, extraFilterParam as string, additionalFilters);

          if (hasUATRecords) {
            filter = combineFilterStrings(filter, 'isUATConsignment eq ' + settings.showUATRecords);
          }

          exportFilterRef.current = filter;

          // Clean empty parentheses before sending to API
          const sanitizeFilter = (filterValue: string) =>
            filterValue === '( )' || filterValue === ' and ( )' ? '' : filterValue;

          if (ignoreDates && enableDatelessQueryPaging) {
            /**
             * Handle getting paged data when ignoreDates is true
             */
            const params: GetSalesOrderQueryParams = {
              pageSize: PAGE_SIZE,
              pageNumber: pageNumber,
              includeMetadata: true,
              filter: sanitizeFilter(filter),
              sort: sortString ?? '',
              showDeleted: filterColumn != 'deletedOn' ? false : true
            };

            const res = await listQueryFn(params);

            if (totalCountRef.current == 0) {
              totalCountRef.current = res?.data?.totalItemCount ?? 0;
            }

            if (!childList) {
              const key = keyExpr ?? 'id';
              uiFlowDispatch(setFlowDataIdArray(res?.data?.items.map((row: any) => String(row?.[key]) ?? '') ?? []));
            }

            return {
              data: res.data?.items ?? [],
              totalCount: res.data?.totalItemCount ?? 0
            };
          } else {
            const params: GetSalesOrderQueryParams = {
              pageSize: ignoreDates ? undefined : PAGE_SIZE,
              pageNumber: ignoreDates ? undefined : pageNumber,
              includeMetadata: !ignoreDates,
              filter: sanitizeFilter(filter),
              sort: sortString ?? '',
              showDeleted: filterColumn != 'deletedOn' ? false : true
            };

            const res = await listQueryFn(params);

            if (totalCountRef.current == 0) {
              totalCountRef.current = ignoreDates ? res?.data?.length ?? 0 : res?.data?.totalItemCount ?? 0;
            }

            if (!childList) {
              const key = keyExpr ?? 'id';
              uiFlowDispatch(
                setFlowDataIdArray(
                  ignoreDates
                    ? res?.data?.map((row: any) => String(row?.[key]) ?? '') ?? []
                    : res?.data?.items.map((row: any) => String(row?.[key]) ?? '') ?? []
                )
              );
            }

            return {
              data: ignoreDates ? res.data ?? [] : res.data?.items ?? [],
              totalCount: ignoreDates ? res.data?.length ?? 0 : res.data?.totalItemCount ?? 0
            };
          }
        }
      }),
    [
      additionalFilter,
      childList,
      enableDatelessQueryPaging,
      excludedSearchProperties,
      extraFilterParam,
      filterColumn,
      filterOverrideColumn,
      filterOverrideDateRange,
      filterRef,
      getCompareType,
      hasUATRecords,
      ignoreDates,
      keyExpr,
      listQueryFn,
      overrideFilter,
      settings.showUATRecords,
      columns,
      state.gridApiRef,
      uiFlowDispatch
    ]
  );

  const defaultActions = useMemo(() => {
    return disableEdit || contained === true || (hasUATRecords && settings.showUATRecords)
      ? { canEdit: false, editToolTip: '' }
      : { editToolTip: '' };
  }, [contained, disableEdit, hasUATRecords, settings.showUATRecords]);

  const filterCollection = useMemo(() => {
    return showFilters ? listFilters : null;
  }, [listFilters, showFilters]);

  useEffect(() => {
    /**
     * Handle if there is a filter in the URL,
     * set filter, filterDates and filterColumn
     */

    if (filterParam) {
      const { dateFrom, dateTo, selectedRange, filterColumn, filterColumnIndex } = parseFilterStringToDateRange(
        filterParam as string
      );

      setFilter(filterParam as string);
      setFilterDates({ dateFrom, dateTo, selectedRange });
      setFilterColumn(filterColumn);
      setOrderByIndex(filterColumnIndex);
    }

    if (extraFilterParam) {
      setAdditionalFilter(extraFilterParam as string);
    }
  }, [extraFilterParam, filterParam, setFilter, setFilterDates]);

  useEffect(() => {
    initStorageV2();
  }, [initStorageV2]);

  useEffect(() => {
    if (filterParam) return;

    setFilter(buildFilterString(filterDates, filterColumn, additionalFilter));
  }, [additionalFilter, buildFilterString, filterColumn, filterDates, filterParam, setFilter]);

  useEffect(() => {
    userEmailRef.current = userData?.userProfile?.email ?? '';
  }, [userData?.userProfile?.email]);

  useEffect(() => {
    /**
     * On mount, subscribe to global panel events and handle the close
     * event from the attachments panel.
     */
    const handlePanelEvent = (event: CustomEvent) => {
      const {} = event;
    };

    const unsubscribe = subscribeToPanelEvent(handlePanelEvent);

    return () => {
      unsubscribe();
    };
  }, []);

  return (
    <Stack
      direction="row"
      alignItems="flex-start"
      sx={{
        height: '100%',
        position: 'relative',
        backgroundColor: theme.palette.mode === 'light' ? 'transparent' : '#1e1e1e'
      }}>
      <Box
        component={'div'}
        sx={{
          width: '100%',
          height: '100%',
          backgroundColor: theme.palette.mode === 'light' ? 'transparent' : '#1e1e1e',
          position: 'relative'
        }}>
        <ImportDropzone wrapperSx={{ height: '100%' }} dragOverlaySx={{ zIndex: 999 }}>
          <UIFlowListNew
            AdvancedSearch={AdvancedSearch}
            hideActions={hideActions}
            showColumnPicker={showColumnPicker}
            disableVirtualization={true}
            pageSize={PAGE_SIZE}
            remoteOperations={remoteOptions}
            customStore={customStore}
            loading={false}
            defaultActions={defaultActions}
            wrapHeader
            scrollMode="standard"
            contained={contained}
            listComponentName={isImpersonating ? `impersonating-${controlname}` : controlname} // Use 'impersonating-' prefix for component name when impersonating to ensure gridState is saved as seperate localStorage keys
            enableExport={showExport && !serverSideExport}
            showGroupingPanel={false}
            showFilterPanel={showFilterPanel}
            showHeaderFilter={showHeaderFilter}
            showRowLines={true}
            showFilterRow={false}
            allowMultiSelect={multiSelect}
            sortingMode="server"
            enableLayoutReset={showLayoutReset}
            hideFooter={hideFooter}
            filter={buildFilterString(filterDates, filterColumn, additionalFilter)}
            filterColumn={filterColumn}
            enableSearch={showSearch}
            enableSort
            headerButtonGroup={headerButtonGroup}
            filterCollection={filterCollection}
            filterDateRange={buildFilterString(filterDates, filterColumn, '')}
            columns={columns}
            onFilterItemSelected={FilterButtonSelected}
            defaultGridState={determineDefaultGridState}
            customActions={customActions}
            disableEffects={disableEffects}
            hideHeader={hideHeader}
            customFilter={customFilter}
            keyExpr={keyExpr ? keyExpr : 'id'}
            editToolTip={editToolTip}
            ignoreDates={ignoreDates}
            height={height}
          />

          <ManifestRenderContainer ref={containerRef} />
        </ImportDropzone>
      </Box>
    </Stack>
  );
};

export default VigoFilteredList;
